import type { KeywordTypeId } from "desktop/src/modules/OutreachContactSearch/constants";
import { defineStore } from "pinia";
import { computed, ref, type UnwrapRef } from "vue";

import type { OutreachContactId } from "shared/composables/useContact";
import { MAX_PAGINATION_LIMIT } from "shared/constants";
import {
  type OutreachAllSortFields,
  type OutreachInfluencerField,
  type OutreachInfluencerSortFields,
  type OutreachOutletField,
  type OutreachOutletSortFields,
} from "shared/helpers/outreach";
import {
  type OutreachContactLocation,
  type OutreachDesignatedMarketArea,
} from "shared/resources";
import { type Nullable, type SortingOrder } from "shared/types";
import { type OutreachTopic } from "shared/types/outreach";

export interface OutreachContactList {
  id: number;
  name: string;
}

interface OutreachContactsPage {
  all: number;
  influencer: number;
  outlet: number;
}

interface OutreachContactsPageSize {
  all: number;
  influencer: number;
  outlet: number;
}

interface OutreachContactsSortBy {
  all: OutreachAllSortFields;
  influencer: OutreachInfluencerSortFields;
  outlet: OutreachOutletSortFields;
}

export type OutreachSearchTab = "outlet" | "influencer" | "all";

interface OutreachContactSortDirection {
  all: SortingOrder;
  influencer: SortingOrder;
  outlet: SortingOrder;
}

export type OutreachContactOperator = "AND" | "OR";

type OutreachAdvancedKeywordRule =
  | {
      id: number;
      keyword: string;
      kind: "keyword";
      typeIds: KeywordTypeId[];
    }
  | {
      id: number;
      keyword: string;
      kind: "location";
      physicalLocationIds: number[];
    }
  | {
      id: number;
      keyword: string;
      kind: "sector";
      outreachTopicIds: number[];
    };

interface OutreachAdvancedKeywordSet {
  id: number;
  operator: OutreachContactOperator;
  rules: OutreachAdvancedKeywordRule[];
}

export interface OutreachSavedSearchState {
  advancedKeywordSets: OutreachAdvancedKeywordSet[];
  filters: Partial<SavedSearchFilters>;
  searchTab: OutreachSearchTab;
  sortBy: OutreachContactsSortBy;
  sortDirection: OutreachContactSortDirection;
  version: 1;
}

interface OutreachCurrentSavedSearch {
  id: string;
  name: string;
  state: OutreachSavedSearchState;
  updatedAt?: string;
}

interface SavedSearchFilters {
  contactListId: Nullable<number>;
  filterCirculation: OutreachContactsCirculation;
  filterContentTypes: OutreachContactsAudienceType[];
  filterCoverages: OutreachContactsCoverage[];
  filterDesignatedMarketAreas: UnwrapRef<OutreachDesignatedMarketArea[]>;
  filterFrequencies: OutreachContactsFrequency[];
  filterLanguages: string[];
  filterLists: OutreachContactList[];
  filterLocations: UnwrapRef<OutreachContactLocation[]>;
  filterMediaTypes: OutreachContactsMediaTypeOption[];
  filterSocials: OutreachContactsSocial[];
  filterTalkingAboutOperator: OutreachContactOperator;
  filterTalkingAboutTags: string[];
  filterTargetAreas: UnwrapRef<OutreachContactLocation[]>;
  filterTiers: OutreachContactsTier[];
  filterTopics: UnwrapRef<OutreachTopic[]>;
  isKeywordTalkingAboutEnabled: boolean;
  keywordInfluencerFields: OutreachInfluencerField[];
  keywordOperator: OutreachContactOperator;
  keywordOutletFields: OutreachOutletField[];
  keywordQuery: string;
  keywordTags: string[];
}

export type OutreachContactsSocial =
  | "Bluesky"
  | "Blog"
  | "Facebook"
  | "Forum"
  | "Instagram"
  | "LinkedIn"
  | "Other"
  | "Pinterest"
  | "Reddit"
  | "Substack"
  | "TikTok"
  | "Twitter"
  | "YouTube";

export type OutreachContactsCoverage =
  | "International"
  | "Local"
  | "National"
  | "Regional";

export type OutreachContactsMediaType =
  | "Blog"
  | "Bureau"
  | "Magazine"
  | "Newsletter"
  | "News Service/Syndicate"
  | "Newspaper"
  | "Podcast"
  | "Radio Network/Station"
  | "Radio Show"
  | "Social"
  | "Television Network/Station"
  | "Television Show"
  | "Website";

interface OutreachContactsMediaTypeOption {
  id: string;
  name: OutreachContactsMediaType;
}

interface OutreachContactsCirculation {
  enabled: boolean;
  gte: Nullable<number>;
  lte: Nullable<number>;
}

export type OutreachContactsFrequency =
  | "Annual"
  | "Bi-Monthly"
  | "Bimonthly"
  | "Daily"
  | "Monthly"
  | "No Frequency Established"
  | "Quarterly"
  | "Semi-Annual"
  | "Semi-Monthly"
  | "Semiannual"
  | "Semimonthly"
  | "Weekly";

export type OutreachContactsAudienceType =
  | "Consumer"
  | "Technical/Scientific/Academic"
  | "Trade";

export type OutreachContactsTier = 1 | 2 | 3 | 4;

const DEFAULT_KEYWORD_OUTLET_FIELDS: OutreachOutletField[] = [
  "name",
  "publisher",
  "address",
  "email",
  "profile",
];

const DEFAULT_KEYWORD_INFLUENCER_FIELDS: OutreachInfluencerField[] = [
  "name",
  "job_title",
  "profile",
  "email",
  "address",
];

function normalizeSavedSearchCriteria(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value
      .map(normalizeSavedSearchCriteria)
      .sort((first, second) =>
        JSON.stringify(first).localeCompare(JSON.stringify(second))
      );
  }

  if (!value || typeof value !== "object") return value;

  const record = value as Record<string, unknown>;

  if (
    (typeof record.id === "string" || typeof record.id === "number") &&
    !("kind" in record) &&
    !("operator" in record) &&
    !("rules" in record)
  ) {
    return { id: String(record.id) };
  }

  return Object.fromEntries(
    Object.entries(record)
      .sort(([firstKey], [secondKey]) => firstKey.localeCompare(secondKey))
      .map(([key, nestedValue]) => [
        key,
        normalizeSavedSearchCriteria(nestedValue),
      ])
  );
}

export const useOutreachContactsStore = defineStore("outreachContacts", () => {
  const page = ref<OutreachContactsPage>({
    all: 1,
    influencer: 1,
    outlet: 1,
  });

  const pageSize = ref<OutreachContactsPageSize>({
    all: 100,
    influencer: 100,
    outlet: 100,
  });

  const totalContacts = ref<number>(0);
  const currentSavedSearch = ref<Nullable<OutreachCurrentSavedSearch>>(null);
  const savedSearchOpenedAt = ref<number>(0);

  const sortBy = ref<OutreachContactsSortBy>({
    all: "relevance",
    influencer: "relevance",
    outlet: "relevance",
  });

  const selectedIds = ref<OutreachContactId[]>([]);
  const advancedKeywordSets = ref<OutreachAdvancedKeywordSet[]>([]);

  const sortDirection = ref<OutreachContactSortDirection>({
    all: "asc",
    influencer: "desc",
    outlet: "desc",
  });

  const keywordQuery = ref<string>("");
  const keywordTags = ref<string[]>([]);
  const keywordOperator = ref<OutreachContactOperator>("OR");

  const keywordOutletFields = ref<OutreachOutletField[]>([
    ...DEFAULT_KEYWORD_OUTLET_FIELDS,
  ]);

  const keywordInfluencerFields = ref<OutreachInfluencerField[]>([
    ...DEFAULT_KEYWORD_INFLUENCER_FIELDS,
  ]);

  const isKeywordTalkingAboutEnabled = ref<boolean>(true);

  const ignoreDefaultCountryFilters = ref<boolean>(false);
  const scrollTop = ref<number>(0);
  const searchTab = ref<OutreachSearchTab>("all");
  const filterLocations = ref<OutreachContactLocation[]>([]);
  const defaultFilterLocations = ref<OutreachContactLocation[]>([]);
  const defaultFilterLocationIds = ref<string[]>([]);
  const filterTalkingAboutTags = ref<string[]>([]);
  const filterTalkingAboutOperator = ref<OutreachContactOperator>("AND");
  const filterTargetAreas = ref<OutreachContactLocation[]>([]);
  const filterTopics = ref<OutreachTopic[]>([]);
  const filterLanguages = ref<string[]>([]);
  const filterSocials = ref<OutreachContactsSocial[]>([]);
  const filterCoverages = ref<OutreachContactsCoverage[]>([]);
  const filterMediaTypes = ref<OutreachContactsMediaTypeOption[]>([]);

  const filterCirculation = ref<OutreachContactsCirculation>({
    enabled: false,
    gte: null,
    lte: null,
  });

  const filterFrequencies = ref<OutreachContactsFrequency[]>([]);
  const filterContentTypes = ref<OutreachContactsAudienceType[]>([]);
  const filterDesignatedMarketAreas = ref<OutreachDesignatedMarketArea[]>([]);
  const filterTiers = ref<OutreachContactsTier[]>([]);
  const filterLists = ref<OutreachContactList[]>([]);
  const contactListId = ref<Nullable<number>>(null);

  const pagination = computed(() => ({
    page: page.value[searchTab.value],
    pageCount: Math.ceil(
      Math.min(totalContacts.value, MAX_PAGINATION_LIMIT) /
        pageSize.value[searchTab.value]
    ),
    pageSize: pageSize.value[searchTab.value],
  }));

  const savedSearchFilters = computed<SavedSearchFilters>(() => ({
    contactListId: contactListId.value,
    filterCirculation: filterCirculation.value,
    filterContentTypes: filterContentTypes.value,
    filterCoverages: filterCoverages.value,
    filterDesignatedMarketAreas: filterDesignatedMarketAreas.value,
    filterFrequencies: filterFrequencies.value,
    filterLanguages: filterLanguages.value,
    filterLists: filterLists.value,
    filterLocations: filterLocations.value,
    filterMediaTypes: filterMediaTypes.value,
    filterSocials: filterSocials.value,
    filterTalkingAboutOperator: filterTalkingAboutOperator.value,
    filterTalkingAboutTags: filterTalkingAboutTags.value,
    filterTargetAreas: filterTargetAreas.value,
    filterTiers: filterTiers.value,
    filterTopics: filterTopics.value,
    isKeywordTalkingAboutEnabled: isKeywordTalkingAboutEnabled.value,
    keywordInfluencerFields: keywordInfluencerFields.value,
    keywordOperator: keywordOperator.value,
    keywordOutletFields: keywordOutletFields.value,
    keywordQuery: keywordQuery.value,
    keywordTags: keywordTags.value,
  }));

  const getFilters = computed(() => ({
    ...savedSearchFilters.value,
    keywordQuery: keywordQuery.value.replaceAll("&", "%26"),
  }));

  const hasUnsavedOutreachSearchCriteria = computed<boolean>(() => {
    const filters = savedSearchFilters.value;

    const arrayFilterKeys: (keyof SavedSearchFilters)[] = [
      "filterContentTypes",
      "filterCoverages",
      "filterDesignatedMarketAreas",
      "filterFrequencies",
      "filterLanguages",
      "filterLists",
      "filterMediaTypes",
      "filterSocials",
      "filterTalkingAboutTags",
      "filterTargetAreas",
      "filterTiers",
      "filterTopics",
      "keywordTags",
    ];

    if (advancedKeywordSets.value.length) return true;
    if (filters.contactListId) return true;
    if (filters.keywordQuery) return true;
    if (filters.filterCirculation?.enabled) return true;

    if (filters.filterCirculation?.gte || filters.filterCirculation?.lte) {
      return true;
    }

    if (filters.isKeywordTalkingAboutEnabled === false) return true;

    if (filters.keywordOperator && filters.keywordOperator !== "OR") {
      return true;
    }

    if (
      filters.filterTalkingAboutOperator &&
      filters.filterTalkingAboutOperator !== "AND"
    ) {
      return true;
    }

    const filterLocationIds = filterLocations.value
      .flatMap((location) => (location.id ? [location.id] : []))
      .sort();

    if (
      JSON.stringify(filterLocationIds) !==
      JSON.stringify(defaultFilterLocationIds.value)
    ) {
      return true;
    }

    return arrayFilterKeys.some((key) => {
      const filterValue = filters[key];

      return Array.isArray(filterValue) && filterValue.length > 0;
    });
  });

  const isCurrentSavedSearchDirty = computed(() => {
    if (!currentSavedSearch.value) return false;

    const currentState = buildSavedSearchState();
    const savedState = currentSavedSearch.value.state;

    return (
      JSON.stringify(
        normalizeSavedSearchCriteria({
          advancedKeywordSets: savedState.advancedKeywordSets,
          filters: savedState.filters,
        })
      ) !==
      JSON.stringify(
        normalizeSavedSearchCriteria({
          advancedKeywordSets: currentState.advancedKeywordSets,
          filters: currentState.filters,
        })
      )
    );
  });

  function clearFilters() {
    page.value = {
      all: 1,
      influencer: 1,
      outlet: 1,
    };

    totalContacts.value = 0;
    selectedIds.value = [];
    advancedKeywordSets.value = [];
    keywordQuery.value = "";
    keywordTags.value = [];
    keywordOperator.value = "OR";

    keywordOutletFields.value = [...DEFAULT_KEYWORD_OUTLET_FIELDS];

    keywordInfluencerFields.value = [...DEFAULT_KEYWORD_INFLUENCER_FIELDS];

    isKeywordTalkingAboutEnabled.value = true;
    ignoreDefaultCountryFilters.value = false;
    scrollTop.value = 0;
    filterLocations.value = [...defaultFilterLocations.value];
    filterTalkingAboutTags.value = [];
    filterTalkingAboutOperator.value = "AND";
    filterTargetAreas.value = [];
    filterTopics.value = [];
    filterLanguages.value = [];
    filterSocials.value = [];
    filterCoverages.value = [];
    filterMediaTypes.value = [];

    filterCirculation.value = {
      enabled: false,
      gte: null,
      lte: null,
    };

    filterFrequencies.value = [];
    filterContentTypes.value = [];
    filterDesignatedMarketAreas.value = [];
    filterTiers.value = [];
    filterLists.value = [];
    contactListId.value = null;
  }

  function startNewSearch() {
    clearFilters();
    currentSavedSearch.value = null;
    savedSearchOpenedAt.value = 0;
  }

  function addSelectedIds(ids: OutreachContactId[]) {
    const selectedIdsSet = new Set(selectedIds.value);
    ids.forEach((id) => selectedIdsSet.add(id));
    selectedIds.value = Array.from(selectedIdsSet);
  }

  function removeSelectedIds(ids: OutreachContactId[]) {
    const selectedIdsSet = new Set(selectedIds.value);
    ids.forEach((id) => selectedIdsSet.delete(id));
    selectedIds.value = Array.from(selectedIdsSet);
  }

  function setSearchTab(tab: OutreachSearchTab) {
    searchTab.value = tab;
  }

  function setSortBy(updatedSortBy: OutreachContactsSortBy) {
    sortBy.value = updatedSortBy;
  }

  function setSortDirection(
    updatedSortDirection: OutreachContactSortDirection
  ) {
    sortDirection.value = updatedSortDirection;
  }

  function setTabSortBy<T extends keyof OutreachContactsSortBy>(
    updatedSortBy: OutreachContactsSortBy[T]
  ) {
    sortBy.value[searchTab.value as T] = updatedSortBy;
  }

  function setInfluencerTabSortBy(updatedSortBy: OutreachInfluencerSortFields) {
    sortBy.value.influencer = updatedSortBy;
  }

  function setTabSortDirection(updatedSortDirection: SortingOrder) {
    sortDirection.value[searchTab.value] = updatedSortDirection;
  }

  function setPage(updatedPage: OutreachContactsPage) {
    page.value = updatedPage;
  }

  function setPagination({
    page: updatedPage,
    pageSize: updatedPageSize,
  }: {
    page: OutreachContactsPage;
    pageSize: OutreachContactsPageSize;
  }) {
    page.value = {
      ...page.value,
      ...updatedPage,
    };

    pageSize.value = {
      ...pageSize.value,
      ...updatedPageSize,
    };
  }

  function setTabPagination({
    page: updatedPage,
    pageSize: updatedPageSize,
  }: {
    page: number;
    pageSize: number;
  }) {
    page.value[searchTab.value] = updatedPage;
    pageSize.value[searchTab.value] = updatedPageSize;
  }

  function setTotalContacts(total: number) {
    totalContacts.value = total;
  }

  function addFilterTopic(topic: OutreachTopic) {
    const foundTopic = filterTopics.value.find(
      (existingTopic) => existingTopic.id === topic.id
    );

    if (foundTopic) return;

    filterTopics.value = [...filterTopics.value, topic];
    scrollTop.value = 0;
    page.value[searchTab.value] = 1;
  }

  function setFilterLocations(locations: OutreachContactLocation[]) {
    filterLocations.value = locations;
  }

  function setDefaultFilterLocations(locations: OutreachContactLocation[]) {
    defaultFilterLocations.value = locations;
    filterLocations.value = [...locations];

    defaultFilterLocationIds.value = locations
      .flatMap((location) => (location.id ? [location.id] : []))
      .sort();
  }

  function removeFilterLocation(location: OutreachContactLocation) {
    filterLocations.value = filterLocations.value.filter(
      (filterLocation) => filterLocation.id !== location.id
    );
  }

  function setFilterTargetAreas(targetAreas: OutreachContactLocation[]) {
    filterTargetAreas.value = targetAreas;
  }

  function removeFilterTargetArea(targetArea: OutreachContactLocation) {
    filterTargetAreas.value = filterTargetAreas.value.filter(
      (filterTargetArea) => filterTargetArea.id !== targetArea.id
    );
  }

  function setFilterTopics(topics: OutreachTopic[]) {
    filterTopics.value = topics;
  }

  function removeFilterTopic(topic: OutreachTopic) {
    filterTopics.value = filterTopics.value.filter(
      (existingTopic) => existingTopic.id !== topic.id
    );
  }

  function setFilterDesignatedMarketAreas(
    designatedMarketAreas: OutreachDesignatedMarketArea[]
  ) {
    filterDesignatedMarketAreas.value = designatedMarketAreas;
  }

  function removeFilterDesignatedMarketArea(dma: OutreachDesignatedMarketArea) {
    filterDesignatedMarketAreas.value =
      filterDesignatedMarketAreas.value.filter(
        (existingDesignatedMarketArea) =>
          existingDesignatedMarketArea.id !== dma.id
      );
  }

  function setFilterLanguages(languages: string[]) {
    filterLanguages.value = languages;
  }

  function removeFilterLanguage(language: string) {
    filterLanguages.value = filterLanguages.value.filter(
      (outletLanguage) => outletLanguage !== language
    );
  }

  function removeFilterTalkingAboutTag(tagIndex: number) {
    filterTalkingAboutTags.value.splice(tagIndex, 1);
  }

  function setFilterSocials(socials: OutreachContactsSocial[]) {
    filterSocials.value = socials;
  }

  function removeFilterSocial(social: OutreachContactsSocial) {
    filterSocials.value = filterSocials.value.filter(
      (filterSocial) => filterSocial !== social
    );
  }

  function setFilterCoverages(coverages: OutreachContactsCoverage[]) {
    filterCoverages.value = coverages;
  }

  function removeFilterCoverages(coverage: OutreachContactsCoverage) {
    filterCoverages.value = filterCoverages.value.filter(
      (filterCoverage) => filterCoverage !== coverage
    );
  }

  function setFilterMediaTypes(mediaTypes: OutreachContactsMediaTypeOption[]) {
    filterMediaTypes.value = mediaTypes;
  }

  function removeFilterMediaType({ id }: { id: string }) {
    filterMediaTypes.value = filterMediaTypes.value.filter(
      (mediaType) => mediaType.id !== id
    );
  }

  function resetFilterCirculation() {
    filterCirculation.value.enabled = false;
    filterCirculation.value.gte = null;
    filterCirculation.value.lte = null;
  }

  function setFilterCirculation({ gte, lte }: OutreachContactsCirculation) {
    let enabled = false;

    if (gte || lte) {
      enabled = true;
    }

    Object.assign(filterCirculation.value, {
      enabled,
      gte,
      lte,
    });
  }

  function setFilterFrequencies(frequencies: OutreachContactsFrequency[]) {
    filterFrequencies.value = frequencies;
  }

  function removeFilterFrequency(frequency: OutreachContactsFrequency) {
    filterFrequencies.value = filterFrequencies.value.filter(
      (filterFrequency) => filterFrequency !== frequency
    );
  }

  function setFilterContentTypes(contentTypes: OutreachContactsAudienceType[]) {
    filterContentTypes.value = contentTypes;
  }

  function removeFilterContentType(contentType: OutreachContactsAudienceType) {
    filterContentTypes.value = filterContentTypes.value.filter(
      (filterContentType) => filterContentType !== contentType
    );
  }

  function setSelectedIds(ids: OutreachContactId[]) {
    selectedIds.value = ids;
  }

  function setKeywordQuery(query: string) {
    keywordQuery.value = query;
  }

  function setKeywordTags(tags: string[]) {
    keywordTags.value = tags;
  }

  function setKeywordOperator(operator: OutreachContactOperator) {
    keywordOperator.value = operator;
  }

  function setKeywordInfluencerFields(fields: OutreachInfluencerField[]) {
    keywordInfluencerFields.value = fields;
  }

  function setKeywordOutletFields(fields: OutreachOutletField[]) {
    keywordOutletFields.value = fields;
  }

  function setIgnoreDefaultCountryFilters(ignore: boolean) {
    ignoreDefaultCountryFilters.value = ignore;
  }

  function setScrollTop(value: number) {
    scrollTop.value = value;
  }

  function setFilterTalkingAboutTags(tags: string[]) {
    filterTalkingAboutTags.value = tags;
  }

  function setFilterTalkingAboutOperator(operator: OutreachContactOperator) {
    filterTalkingAboutOperator.value = operator;
  }

  function setIsKeywordTalkingAboutEnabled(value: boolean) {
    isKeywordTalkingAboutEnabled.value = value;
  }

  function setFilterLists(lists: OutreachContactList[]) {
    filterLists.value = lists;
    scrollTop.value = 0;
    page.value[searchTab.value] = 1;
  }

  function setContactListId(id: Nullable<number>) {
    contactListId.value = id;
  }

  function removeFilterList(list: OutreachContactList) {
    filterLists.value = filterLists.value.filter(
      (filterList) => filterList.id !== list.id
    );

    scrollTop.value = 0;
    page.value[searchTab.value] = 1;
  }

  function setFilterTiers(tiers: OutreachContactsTier[]) {
    filterTiers.value = tiers;
    scrollTop.value = 0;
    page.value[searchTab.value] = 1;
  }

  function removeFilterTier(tier: OutreachContactsTier) {
    filterTiers.value = filterTiers.value.filter(
      (filterTier) => filterTier !== tier
    );

    scrollTop.value = 0;
    page.value[searchTab.value] = 1;
  }

  function toggleFilterTier(tier: OutreachContactsTier) {
    const index = filterTiers.value.indexOf(tier);

    if (index > -1) {
      removeFilterTier(tier);
    } else {
      setFilterTiers([...filterTiers.value, tier]);
    }
  }

  function setAdvancedKeywordSets(keywordSets: OutreachAdvancedKeywordSet[]) {
    advancedKeywordSets.value = keywordSets;
  }

  function setCurrentSavedSearch(savedSearch: OutreachCurrentSavedSearch) {
    currentSavedSearch.value = savedSearch;
  }

  function applySavedSearch(savedSearch: OutreachCurrentSavedSearch) {
    const state = JSON.parse(JSON.stringify(savedSearch.state));
    const filters = state.filters ?? {};

    advancedKeywordSets.value = state.advancedKeywordSets ?? [];
    contactListId.value = filters.contactListId ?? null;

    filterCirculation.value = filters.filterCirculation ?? {
      enabled: false,
      gte: null,
      lte: null,
    };

    filterContentTypes.value = filters.filterContentTypes ?? [];
    filterCoverages.value = filters.filterCoverages ?? [];

    filterDesignatedMarketAreas.value =
      filters.filterDesignatedMarketAreas ?? [];

    filterFrequencies.value = filters.filterFrequencies ?? [];
    filterLanguages.value = filters.filterLanguages ?? [];
    filterLists.value = filters.filterLists ?? [];
    filterLocations.value = filters.filterLocations ?? [];
    filterMediaTypes.value = filters.filterMediaTypes ?? [];
    filterSocials.value = filters.filterSocials ?? [];

    filterTalkingAboutOperator.value =
      filters.filterTalkingAboutOperator ?? "AND";

    filterTalkingAboutTags.value = filters.filterTalkingAboutTags ?? [];
    filterTargetAreas.value = filters.filterTargetAreas ?? [];
    filterTiers.value = filters.filterTiers ?? [];
    filterTopics.value = filters.filterTopics ?? [];

    isKeywordTalkingAboutEnabled.value =
      filters.isKeywordTalkingAboutEnabled ?? true;

    keywordInfluencerFields.value = filters.keywordInfluencerFields ?? [
      ...DEFAULT_KEYWORD_INFLUENCER_FIELDS,
    ];

    keywordOperator.value = filters.keywordOperator ?? "OR";

    keywordOutletFields.value = filters.keywordOutletFields ?? [
      ...DEFAULT_KEYWORD_OUTLET_FIELDS,
    ];

    keywordQuery.value = filters.keywordQuery ?? "";
    keywordTags.value = filters.keywordTags ?? [];

    page.value = {
      all: 1,
      influencer: 1,
      outlet: 1,
    };

    pageSize.value = {
      all: 100,
      influencer: 100,
      outlet: 100,
    };

    searchTab.value = state.searchTab ?? "all";

    sortBy.value = state.sortBy ?? {
      all: "relevance",
      influencer: "relevance",
      outlet: "relevance",
    };

    sortDirection.value = state.sortDirection ?? {
      all: "asc",
      influencer: "desc",
      outlet: "desc",
    };

    ignoreDefaultCountryFilters.value = true;
    scrollTop.value = 0;
    selectedIds.value = [];
    totalContacts.value = 0;

    currentSavedSearch.value = {
      ...savedSearch,
      state: buildSavedSearchState(),
    };

    savedSearchOpenedAt.value = Date.now();
  }

  function buildSavedSearchState(): OutreachSavedSearchState {
    return JSON.parse(
      JSON.stringify({
        advancedKeywordSets: advancedKeywordSets.value,
        filters: savedSearchFilters.value,
        searchTab: searchTab.value,
        sortBy: sortBy.value,
        sortDirection: sortDirection.value,
        version: 1,
      })
    );
  }

  return {
    addFilterTopic,
    addSelectedIds,
    advancedKeywordSets,
    applySavedSearch,
    buildSavedSearchState,
    clearFilters,
    contactListId,
    currentSavedSearch,
    filterCirculation,
    filterContentTypes,
    filterCoverages,
    filterDesignatedMarketAreas,
    filterFrequencies,
    filterLanguages,
    filterLists,
    filterLocations,
    filterMediaTypes,
    filterSocials,
    filterTalkingAboutOperator,
    filterTalkingAboutTags,
    filterTargetAreas,
    filterTiers,
    filterTopics,
    getFilters,
    hasUnsavedOutreachSearchCriteria,
    ignoreDefaultCountryFilters,
    isCurrentSavedSearchDirty,
    isKeywordTalkingAboutEnabled,
    keywordInfluencerFields,
    keywordOperator,
    keywordOutletFields,
    keywordQuery,
    keywordTags,
    page,
    pageSize,
    pagination,
    removeFilterContentType,

    removeFilterCoverages,
    removeFilterDesignatedMarketArea,

    removeFilterFrequency,
    removeFilterLanguage,
    removeFilterList,
    removeFilterLocation,
    removeFilterMediaType,
    removeFilterSocial,
    removeFilterTalkingAboutTag,
    removeFilterTargetArea,
    removeFilterTier,
    removeFilterTopic,
    removeSelectedIds,
    resetFilterCirculation,
    savedSearchOpenedAt,
    scrollTop,
    searchTab,
    selectedIds,
    setAdvancedKeywordSets,
    setContactListId,
    setCurrentSavedSearch,
    setDefaultFilterLocations,
    setFilterCirculation,
    setFilterContentTypes,
    setFilterCoverages,
    setFilterDesignatedMarketAreas,
    setFilterFrequencies,
    setFilterLanguages,
    setFilterLists,
    setFilterLocations,
    setFilterMediaTypes,
    setFilterSocials,
    setFilterTalkingAboutOperator,
    setFilterTalkingAboutTags,
    setFilterTargetAreas,
    setFilterTiers,
    setFilterTopics,
    setIgnoreDefaultCountryFilters,
    setInfluencerTabSortBy,
    setIsKeywordTalkingAboutEnabled,
    setKeywordInfluencerFields,
    setKeywordOperator,
    setKeywordOutletFields,
    setKeywordQuery,
    setKeywordTags,
    setPage,
    setPagination,
    setScrollTop,
    setSearchTab,
    setSelectedIds,
    setSortBy,
    setSortDirection,
    setTabPagination,
    setTabSortBy,
    setTabSortDirection,
    setTotalContacts,
    sortBy,
    sortDirection,
    startNewSearch,
    toggleFilterTier,
    totalContacts,
  };
});

export default useOutreachContactsStore;
